import { ActivationStatus, IAccount, IOperationResultNoValue, NoteViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import {
	AccountAdminViewModel,
	AccountSnapshotViewModel,
	AdminPermission,
} from '../../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../../web/components/Checkbox';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../web/components/HoverCard';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { Button } from '../../../../web/components-v2/Button';
import { urgentDate } from '../../../../web/styles/colors';
import { invalidateMonthlyRenewalQueries } from '../../../containers/reports/MonthlyRenewalAccounts';
import { AccountSummaryHoverNote } from '../../AccountSummaryNotePopover';
import { ConditionalAccess } from '../../ConditionalAccess';
import { WillRenewPredictionSelect } from '../../WillRenewPredictionSelect';
import { styleSheet } from './styles';

interface IProps
	extends RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IEventLoggingComponentProps,
		IAdminUserSessionComponentProps {
	account?: AccountAdminViewModel;
	disabled?: boolean;
	onDeleteClicked?(): void;
	onEditCsmClicked?(): void;
	onRequestRefreshRenewData?(): void;
	snapshot?: AccountSnapshotViewModel;
}

interface IState {
	probabilityValue?: string;
	discountValue?: string;
	showNextMeetingPopover?: boolean;
}

class _ReportRowAccount extends React.Component<IProps, IState> {
	private mNoteVM: NoteViewModel;
	private mCsmNote: NoteViewModel;
	private mAccountVM: AccountAdminViewModel;
	public constructor(props: IProps) {
		super(props);

		makeObservable<_ReportRowAccount, 'mNoteVM' | 'mCsmNote' | 'mAccountVM'>(this, {
			mNoteVM: observable.ref,
			mCsmNote: observable.ref,
			mAccountVM: observable.ref,
		});

		const renewal =
			props?.snapshot?.updatedRenewalProbability ??
			props?.snapshot?.accountModel?.additionalInfo?.renewalProbability ??
			props?.account?.toJs()?.additionalInfo?.renewalProbability;

		const discount =
			props?.snapshot?.updatedRenewalDiscountPercentage ??
			props?.snapshot?.accountModel?.additionalInfo?.renewalDiscountPercentage ??
			props?.account?.toJs()?.additionalInfo?.renewalDiscountPercentage;

		this.state = {
			probabilityValue: renewal ? renewal?.toLocaleString() + '%' : '0%',
			discountValue: discount ? discount?.toLocaleString() + '%' : '0%',
		};
		this.mAccountVM = props?.snapshot
			? new AccountAdminViewModel(props.userSession, props.snapshot?.accountModel)
			: props?.account;
		this.mNoteVM = new NoteViewModel(props.userSession, {
			id: props.account?.toJs()?.additionalInfo?.summaryNoteId ?? props?.snapshot?.currentSummaryNoteId,
		});
		this.mCsmNote = new NoteViewModel(props.userSession, {
			id: props.account?.toJs()?.additionalInfo?.csmNoteId ?? props?.snapshot?.currentCsmNoteId,
		});
	}

	public componentDidUpdate(prevProps: IProps) {
		// The constructor caches mAccountVM once. When the parent feeds a new snapshot
		// (e.g. after a react-query cache replacement on CS specialist edit), refresh
		// the wrapped VM so the row reflects the new account data.
		const nextAccountModel = this.props.snapshot?.accountModel;
		const prevAccountModel = prevProps.snapshot?.accountModel;
		if (this.props.snapshot && nextAccountModel && nextAccountModel !== prevAccountModel) {
			runInAction(() => {
				this.mAccountVM = new AccountAdminViewModel(this.props.userSession, nextAccountModel);
			});
		}
	}

	public render() {
		const { account, snapshot, onDeleteClicked, disabled } = this.props;
		const { probabilityValue, discountValue } = this.state;
		const accountVM = this.mAccountVM?.toJs();
		let colorClass;
		if (accountVM?.usage?.statusColor) {
			const color = accountVM.usage.statusColor.toLocaleLowerCase();
			switch (color) {
				case 'green':
					colorClass = styleSheet.green;
					break;
				case 'yellow':
					colorClass = styleSheet.yellow;
					break;
				case 'orange':
					colorClass = styleSheet.orange;
					break;
				case 'red':
					colorClass = styleSheet.red;
					break;
				default:
					break;
			}
		}

		const pastDue = snapshot?.toJs()?.currentBillingStatus?.toLocaleLowerCase() === 'past_due';
		const cancelled =
			!pastDue && (snapshot?.accountCancelled || this.mAccountVM?.activationStatus === ActivationStatus.CANCELED);
		const cancellationPending = !pastDue && !cancelled && !!snapshot?.pendingCancellation;

		const initialRenewProbability = snapshot?.accountModel?.additionalInfo?.renewalProbability || 0;
		const initialDiscountPercentage = snapshot?.accountModel?.additionalInfo?.renewalDiscountPercentage || 0;

		let renewalPdfWasSent = false;
		const renewalPdfSent = snapshot?.renewalPdfSent ?? accountVM?.additionalInfo?.renewalPdfSent;
		if (renewalPdfSent) {
			const diferenceInMs = moment().diff(moment(renewalPdfSent));
			const duration = moment.duration(diferenceInMs);
			renewalPdfWasSent = duration.asDays() < 90;
		}

		return (
			<div className={`${css(styleSheet.container)} group`}>
				<ConditionalAccess permission={AdminPermission.CreateAndDeleteSnapshots}>
					{snapshot && (
						<div className={css(styleSheet.deleteSnapshot)} onClick={onDeleteClicked}>
							<TrashIcon fillColor={urgentDate} />
						</div>
					)}
				</ConditionalAccess>
				<div
					className={css(
						styleSheet.rowContainerInner,
						snapshot?.accountDeleted || disabled ? styleSheet.dim : undefined
					)}
				>
					{snapshot?.accountDeleted || disabled ? (
						<div className={css(styleSheet.accountName, styleSheet.disablePointer)}>
							<span>{accountVM?.companyName || ''}</span>
							{pastDue && <div className={css(styleSheet.pastDue)}> PAST DUE</div>}
							{cancelled && <div className={css(styleSheet.cancelled)}>CANCELLED</div>}
							{cancellationPending && <div className={css(styleSheet.cancelled)}>CANCELLATION PENDING</div>}
						</div>
					) : (
						<HoverCard openDelay={300}>
							<HoverCardTrigger asChild>
								<NavLink
									className={css(styleSheet.accountName, styleSheet.accountNameHC)}
									to={'/accounts/' + accountVM?.id}
									onMouseEnter={this.loadNote}
								>
									<span className={css(styleSheet.status, colorClass)} />
									{accountVM?.companyName || ''}
									{pastDue && <div className={css(styleSheet.pastDue)}> PAST DUE</div>}
									{cancelled && <div className={css(styleSheet.cancelled)}>CANCELLED</div>}
									{cancellationPending && <div className={css(styleSheet.cancelled)}>CANCELLATION PENDING</div>}
								</NavLink>
							</HoverCardTrigger>
							<HoverCardContent>
								<AccountSummaryHoverNote account={accountVM} summaryNote={this.mNoteVM} csmNote={this.mCsmNote} />
							</HoverCardContent>
						</HoverCard>
					)}
					<div className={css(styleSheet.renewalDate)}>
						{accountVM?.planDetails?.renewalDate ? moment(accountVM.planDetails.renewalDate).format('MM-DD') : ''}
					</div>
					<div className={css(styleSheet.renewalDate)}>
						{accountVM?.planDetails?.billingCycle ? accountVM.planDetails.billingCycle.toString() + ' mo.' : ''}
					</div>
					<div className={css(styleSheet.renewalDate, accountVM?.planDetails?.planMRR >= 900 && styleSheet.mrr)}>
						{accountVM?.planDetails?.planMRR ? '$' + accountVM.planDetails.planMRR.toLocaleString() : ''}
					</div>
					<div className={css(styleSheet.csm)}>
						<span>
							{accountVM?.additionalInfo?.customerSuccessName ? (
								accountVM.additionalInfo.customerSuccessName.split(' ')[0] +
								' ' +
								accountVM.additionalInfo.customerSuccessName.split(' ')[1][0] +
								'.'
							) : (
								<span className={css(styleSheet.unassigned)}>Unassigned</span>
							)}
						</span>
						{!!snapshot && !snapshot.accountDeleted && !!this.props.onEditCsmClicked && (
							<ConditionalAccess permission={AdminPermission.ModifyAccount}>
								<Button
									variant='ghost'
									size='icon'
									aria-label='Edit Customer Success specialist'
									title='Edit Customer Success specialist'
									styles={[styleSheet.csmEditBtn]}
									onClick={this.props.onEditCsmClicked}
								>
									<EditItemPenIcon />
								</Button>
							</ConditionalAccess>
						)}
					</div>
					<div className={css(styleSheet.columnItem)}>
						<span
							className={css(
								styleSheet.percentagePrev,
								this.getPercentageStyle(parseInt(probabilityValue.replace('%', ''), 10), initialRenewProbability)
							)}
						>{`${initialRenewProbability}%`}</span>
						{(!!snapshot && snapshot.canEdit && !snapshot.accountDeleted) || !!account ? (
							<ConditionalAccess
								elseChildren={this.renderPercentageText(
									accountVM,
									snapshot?.updatedRenewalProbability,
									'renewalProbability'
								)}
								permission={AdminPermission.ModifyAccount}
							>
								{!!snapshot?.isLoading || this.mAccountVM?.isUpdatingRenewalProbability ? (
									<LoadingSpinner type='tiny' />
								) : (
									<TextInput
										className={css(styleSheet.probabilityInput)}
										inputClassName={css(styleSheet.probabilityInputText)}
										inputId={`renewal-probability-text-input-${accountVM?.id}`}
										onBlur={this.onInputBlurRenewalPercentage}
										onChange={this.onRenewalProbabilityChanged}
										onFocus={() => this.onInputFocus('probabilityValue')}
										step='1'
										type='number'
										value={parseInt(probabilityValue.replace('%', ''), 10) || ''}
									/>
								)}
							</ConditionalAccess>
						) : (
							this.renderPercentageText(accountVM, snapshot?.updatedRenewalProbability, 'renewalProbability')
						)}
					</div>
					<div className={css(styleSheet.columnItem)}>
						<span
							className={css(styleSheet.percentagePrev, styleSheet.grayProbability)}
						>{`${initialDiscountPercentage}%`}</span>
						{(!!snapshot && snapshot.canEdit && !snapshot.accountDeleted) || !!account ? (
							<ConditionalAccess
								elseChildren={this.renderPercentageText(
									accountVM,
									snapshot?.updatedRenewalDiscountPercentage,
									'renewalDiscountPercentage'
								)}
								permission={AdminPermission.ModifyAccount}
							>
								{!!snapshot?.isLoading || this.mAccountVM?.isUpdatingRenewalDiscountPercentage ? (
									<LoadingSpinner type='tiny' />
								) : (
									<TextInput
										className={css(styleSheet.probabilityInput)}
										inputClassName={css(styleSheet.probabilityInputText)}
										inputId={`renewal-discount-percentage-text-input-${accountVM?.id}`}
										onBlur={this.onInputBlurRenewalDiscountPercentage}
										onChange={this.onDiscountPercentChanged}
										onFocus={() => this.onInputFocus('discountValue')}
										step='0.1'
										type='number'
										value={this.parsePercentageValue(discountValue)}
									/>
								)}
							</ConditionalAccess>
						) : (
							this.renderPercentageText(accountVM, snapshot?.updatedRenewalProbability, 'renewalDiscountPercentage')
						)}
					</div>
					<div className={css(styleSheet.columnItem)}>
						<WillRenewPredictionSelect
							account={this.props.account?.toJs()}
							snapshot={this.props.snapshot}
							errorMessagesVM={this.props.errorMessages}
							renderReasonIndicator
						/>
					</div>
					<div className={css(styleSheet.renewalPdfSent)}>
						{this.mAccountVM?.isUpdatingRenewalPdfSent ? (
							<LoadingSpinner type='tiny' />
						) : (
							<Checkbox
								id={`pdf-sent-checkbox-${accountVM?.id}`}
								type='large'
								onChange={this.onChangedPdfCheck}
								checked={renewalPdfWasSent}
							/>
						)}
					</div>
				</div>
			</div>
		);
	}

	private loadNote = () => {
		if (this.mNoteVM.id && !this.mNoteVM.plainTextContent) {
			this.mNoteVM.load()?.catch(() => {
				/* swallow this error without notifying the user. Not much we can do.*/
			});
		}
	};

	private renderPercentageText(
		account: IAccount,
		updatedPercentageProbability: number = null,
		type: 'renewalProbability' | 'renewalDiscountPercentage'
	) {
		return (
			<div className={css(styleSheet.columnItem)}>
				{updatedPercentageProbability
					? updatedPercentageProbability.toLocaleString() + '%'
					: account?.additionalInfo?.[type]
						? account.additionalInfo[type].toLocaleString() + '%'
						: '0%'}
			</div>
		);
	}

	private onInputFocus = (type: 'probabilityValue' | 'discountValue') => {
		const { [type]: value } = this.state;
		if (type === 'probabilityValue') {
			const numericValue = parseInt(value.replace('%', ''), 10);
			return this.isPercentageValueValid(numericValue)
				? this.setState({ [type]: numericValue.toString() })
				: this.setState({ [type]: '' });
		} else {
			const numericValue = this.parsePercentageValue(value);
			return this.isPercentageValueValid(numericValue)
				? this.setState({ [type]: numericValue.toString() })
				: this.setState({ [type]: '' });
		}
	};

	private onInputBlurRenewalPercentage = () => {
		const { errorMessages, onRequestRefreshRenewData, snapshot, logApiError } = this.props;
		const value = parseInt(this.state.probabilityValue.replace('%', ''), 10);

		if (snapshot) {
			if (snapshot?.updatedRenewalProbability !== value) {
				snapshot
					.updateRenewalProbability(value)
					?.then(() => {
						invalidateMonthlyRenewalQueries();
						onRequestRefreshRenewData?.();
					})
					?.catch((err: IOperationResultNoValue) => {
						errorMessages.pushApiError(err);
						logApiError('SnapshotUpdateRenewalProbability-Error', err);
					});
			}
		} else {
			if (this.mAccountVM?.toJs()?.additionalInfo?.renewalProbability !== value) {
				this.mAccountVM
					?.updateRenewalProbability(value)
					?.then(() => {
						invalidateMonthlyRenewalQueries();
						onRequestRefreshRenewData?.();
					})
					?.catch((err: IOperationResultNoValue) => {
						errorMessages.pushApiError(err);
						logApiError('AccountUpdateRenewalProbability-Error', err);
					});
			}
		}

		if (value >= 0 && value <= 100 && !isNaN(value)) {
			this.setState({ probabilityValue: value.toLocaleString() + '%' });
		} else {
			this.setState({ probabilityValue: '0%' });
		}
	};

	private onInputBlurRenewalDiscountPercentage = () => {
		const { errorMessages, snapshot, logApiError } = this.props;
		const value = this.parsePercentageValue(this.state.discountValue);

		if (snapshot) {
			if (snapshot?.updatedRenewalDiscountPercentage !== value) {
				snapshot
					.updateRenewalDiscountPercentage(value)
					?.then(invalidateMonthlyRenewalQueries)
					?.catch((err: IOperationResultNoValue) => {
						errorMessages.pushApiError(err);
						logApiError('SnapshotUpdateRenewalDiscountPercentage-Error', err);
					});
			}
		} else {
			if (this.mAccountVM?.toJs()?.additionalInfo?.renewalDiscountPercentage !== value) {
				this.mAccountVM
					?.updateRenewalDiscountPercentage(value)
					?.then(invalidateMonthlyRenewalQueries)
					?.catch((err: IOperationResultNoValue) => {
						errorMessages.pushApiError(err);
						logApiError('AccountUpdateRenewalDiscountPercentage-Error', err);
					});
			}
		}

		if (value >= 0 && value <= 100 && !isNaN(value)) {
			this.setState({ discountValue: value.toLocaleString() + '%' });
		} else {
			this.setState({ discountValue: '0%' });
		}
	};

	private onRenewalProbabilityChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.currentTarget.value, 10);
		return this.isPercentageValueValid(value)
			? this.setState({ probabilityValue: value.toLocaleString() })
			: this.setState({ probabilityValue: '' });
	};
	private onDiscountPercentChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseFloat(e.currentTarget.value);
		return this.isPercentageValueValid(value)
			? this.setState({ discountValue: value.toLocaleString() })
			: this.setState({ discountValue: '' });
	};

	private parsePercentageValue = (value: string) => {
		// Remove % symbol if present and parse as float
		const cleanValue = value?.replace('%', '') || '';
		return parseFloat(cleanValue);
	};

	private isPercentageValueValid = (value: number) => {
		return value >= 0 && value <= 100 && !isNaN(value);
	};

	private getPercentageStyle(newPercentage: number, initialPercentage: number) {
		if (newPercentage === initialPercentage) {
			return styleSheet.grayProbability;
		} else if (newPercentage > initialPercentage) {
			return styleSheet.greenProbability;
		} else {
			return styleSheet.redProbability;
		}
	}

	private onChangedPdfCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountVM.updateRenewalPdfSent(e.target.checked ? new Date() : null)?.catch(error => {
			logApiError('SetRenewalPdfSent-Error', error);
			errorMessages.pushApiError(error);
		});
	};
}

const ReportRowAccountAsObserver = observer(_ReportRowAccount);
const ReportRowAccountWithRouter = withRouter(ReportRowAccountAsObserver);
const ReportRowAccountWithLogging = withEventLogging(ReportRowAccountWithRouter);
export const ReportRowAccount = inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(ReportRowAccountWithLogging);
